import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import DashboardHeader from "../components/DashboardHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Receipt, Loader2, TrendingDown } from "lucide-react";

export default function Expenses() {
  const { user } = useAuth();
  const { format } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CVE");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses");
      setItems(data || []);
    } catch { toast.error("Erro ao carregar despesas"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar…</div>;
  if (user === null) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { toast.error("Descreve a despesa"); return; }
    if (!(Number(amount) > 0)) { toast.error("Valor inválido"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", { description: description.trim(), amount: Number(amount), currency });
      toast.success("Despesa registada");
      setDescription("");
      setAmount("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/expenses/${deleteId}`);
      toast.success("Despesa eliminada");
      setDeleteId(null);
      load();
    } catch { toast.error("Erro ao eliminar"); }
  };

  const total = items.reduce((a, i) => a + (Number(i.amount) || 0), 0);
  const totalMonth = items
    .filter((i) => (i.created_at || "").slice(0, 7) === new Date().toISOString().slice(0, 7))
    .reduce((a, i) => a + (Number(i.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-background" data-testid="expenses-page">
      <DashboardHeader title="Despesas" />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="lb-fade-in">
          <div className="lb-overline">Envio, alfândega, bidão, transporte</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1">Despesas</h1>
          <p className="text-sm text-muted-foreground mt-1">Regista aqui todas as despesas de logística (envio Portugal→Cabo Verde, alfândega, bidão, etc.). São descontadas do lucro no painel financeiro.</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-white lb-shadow-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Este mês
            </div>
            <div className="mt-1 font-display text-2xl text-[hsl(var(--destructive))]" data-testid="expenses-month">{format(totalMonth)}</div>
          </div>
          <div className="rounded-2xl bg-white lb-shadow-card p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
              <Receipt className="w-3 h-3" /> Total acumulado
            </div>
            <div className="mt-1 font-display text-2xl" data-testid="expenses-total">{format(total)}</div>
          </div>
        </div>

        {/* Add form */}
        <form onSubmit={submit} className="bg-white rounded-2xl lb-shadow-card p-4 sm:p-5 space-y-3">
          <div className="lb-overline">Nova despesa</div>
          <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-3 sm:items-end">
            <div className="space-y-1">
              <Label className="text-xs">Descrição</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Envio marítimo, alfândega, bidão…" data-testid="expense-description-input" />
            </div>
            <div className="space-y-1 sm:w-32">
              <Label className="text-xs">Valor</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" data-testid="expense-amount-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Moeda</Label>
              <div className="inline-flex rounded-md border border-border bg-white p-1 h-10" data-testid="expense-currency-toggle">
                <button type="button" onClick={() => setCurrency("CVE")} className={`px-3 rounded text-sm font-semibold ${currency === "CVE" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground"}`} data-testid="expense-currency-cve">CVE</button>
                <button type="button" onClick={() => setCurrency("EUR")} className={`px-3 rounded text-sm font-semibold ${currency === "EUR" ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground"}`} data-testid="expense-currency-eur">€</button>
              </div>
            </div>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58] text-white h-10" data-testid="add-expense-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Adicionar
            </Button>
          </div>
        </form>

        {/* List */}
        {loading ? (
          <div className="space-y-2">{Array.from({length:3}).map((_,i)=><div key={i} className="h-14 bg-white rounded-xl lb-shadow-card animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl lb-shadow-card" data-testid="empty-expenses">
            <Receipt className="w-12 h-12 mx-auto text-muted-foreground/50" />
            <p className="mt-3 text-muted-foreground text-sm">Ainda sem despesas registadas.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-xl lb-shadow-card p-3 flex items-center gap-3" data-testid={`expense-row-${it.id}`}>
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--destructive))]/10 flex items-center justify-center shrink-0">
                  <Receipt className="w-4 h-4 text-[hsl(var(--destructive))]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{it.description}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(it.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div className="font-display text-lg text-[hsl(var(--destructive))]">− {format(it.amount)}</div>
                <Button variant="ghost" size="icon" onClick={() => setDeleteId(it.id)} data-testid={`delete-expense-${it.id}`}>
                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-[hsl(var(--destructive))]" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar despesa?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[hsl(var(--destructive))] hover:bg-[#B03838]" data-testid="confirm-delete-expense">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
